// RigSentinel — Drilling Fluid Management (Phase RCE-3)
//
// MUD PROGRAM EDITOR · /wells/:wellId/mud-programs/new and /:id
// Form for one Mud Program: header (name, basis, status, notes) + a list of
// intervals, each with a tri-state property table (target / current /
// planned) for every registered mud property.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Plus, FlaskConical } from "lucide-react";
import RceEditorStrip from "@/components/rce/RceEditorStrip";
import { toast } from "sonner";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import {
  emptyMudProgram, emptyMudProgramInterval,
  createMudProgram, updateMudProgram, getMudProgram, deleteMudProgram,
  FLUID_BASES, MUD_PROGRAM_STATUSES,
} from "@/lib/mud/program";
import { MUD_PROPERTY_KEYS, MUD_PROPERTY_LABELS, emptyPropertyTable } from "@/lib/mud/properties";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import { nextWellSectionHash } from "@/lib/well/completion";

export default function MudProgramEditor() {
  const navigate = useNavigate();
  const { stackMode } = useStackMode();
  const { wellId, id } = useParams();
  const isNew = !id;
  const well = useMemo(() => getWellProfile(wellId), [wellId]);
  const [draft, setDraft] = useState(() => {
    if (isNew) {
      return { ...emptyMudProgram(), wellId, name: "Mud Program v1" };
    }
    const rec = getMudProgram(id);
    return rec || { ...emptyMudProgram(), wellId };
  });

  useEffect(() => {
    if (!isNew) {
      const rec = getMudProgram(id);
      if (rec) setDraft(rec);
    }
  }, [id, isNew]);

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-3)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-3)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its mud program, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  const onSave = () => {
    try {
      const safe = {
        ...draft,
        intervals: (draft.intervals || []).map((iv, i) => ({
          ...iv, id: iv.id || `iv_${i + 1}`,
        })),
      };
      if (isNew) {
        const created = createMudProgram(safe);
        toast.success("Mud Program created");
        unsaved.markSaved(safe);
        const returnToWell = new URLSearchParams(window.location.search).get("return") === "well";
        if (returnToWell) {
          navigate(nextWellSectionHash(wellId, "mud"));
        } else {
          navigate(`/wells/${wellId}/mud-programs/${created.id}`, { replace: true });
        }
      } else {
        updateMudProgram(id, safe);
        toast.success("Mud Program updated");
        unsaved.markSaved(safe);
        const returnToWell = new URLSearchParams(window.location.search).get("return") === "well";
        if (returnToWell) navigate(nextWellSectionHash(wellId, "mud"));
      }
      return true;
    } catch (e) {
      const msg = (e.validationErrors || []).join("\n") || e.message;
      toast.error(msg || "Failed to save Mud Program");
      return false;
    }
  };

  // RC-1 · Unsaved Changes Protection (presentation only).
  // Declared after onSave so its callback references resolve at call time.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const unsaved = useUnsavedChanges({ data: draft, enabled: true, onSave });

  const onDelete = () => {
    if (isNew) return;
    if (!window.confirm("Delete this Mud Program?")) return;
    deleteMudProgram(id);
    navigate(`/wells/${wellId}/mud-programs`);
  };

  const addInterval = () => {
    setDraft((d) => ({ ...d, intervals: [...d.intervals, { ...emptyMudProgramInterval(`Interval ${d.intervals.length + 1}`), id: `iv_${Date.now()}` }] }));
  };
  const removeInterval = (idx) => {
    setDraft((d) => ({ ...d, intervals: d.intervals.filter((_, i) => i !== idx) }));
  };
  const updateInterval = (idx, patch) => {
    setDraft((d) => ({ ...d, intervals: d.intervals.map((iv, i) => i === idx ? { ...iv, ...patch } : iv) }));
  };
  const updateTri = (idx, key, leg, value) => {
    setDraft((d) => ({
      ...d,
      intervals: d.intervals.map((iv, i) => i === idx ? {
        ...iv,
        properties: {
          ...(iv.properties || emptyPropertyTable()),
          [key]: { ...(iv.properties?.[key] || { target: "", current: "", planned: "" }), [leg]: value },
        },
      } : iv),
    }));
  };

  return (
    <div data-testid="mud-program-editor" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/mud-programs`)}
          data-testid="mud-program-editor-back"
          className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Mud Programs
        </button>
        <div className="flex gap-2">
          {!isNew && (
            <button
              data-testid="mud-program-editor-delete"
              onClick={onDelete}
              className="btn-ghost h-9 w-9 rounded inline-flex items-center justify-center"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4 text-rose-400" />
            </button>
          )}
        </div>
      </header>

      <div>
        <h1 className="font-heading font-black text-xl flex items-center gap-2">
          <FlaskConical className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
          {isNew ? "New Mud Program" : draft.name || "Mud Program"}
        </h1>
        <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
          Well: {well.wellName}
        </div>
      </div>

      <RceEditorStrip
        ownerKey="mudProgram"
        ownerLabel="Mud Program · Engineering Context"
        consumes={["project.operator", "rig.name", "well.name", "mud.fluidBasis"]}
      />

      {/* Header */}
      <section className="card-rs p-4 space-y-3">
        <Field
          label="Name"
          testid="mp-name"
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Fluid Basis"
            testid="mp-fluidBasis"
            value={draft.fluidBasis}
            onChange={(v) => setDraft({ ...draft, fluidBasis: v })}
            options={FLUID_BASES.map((b) => ({ value: b, label: b }))}
          />
          <SelectField
            label="Status"
            testid="mp-status"
            value={draft.status}
            onChange={(v) => setDraft({ ...draft, status: v })}
            options={MUD_PROGRAM_STATUSES.map((s) => ({ value: s, label: s }))}
          />
        </div>
        <Field
          label="Notes"
          testid="mp-notes"
          value={draft.notes}
          onChange={(v) => setDraft({ ...draft, notes: v })}
          multiline
        />
      </section>

      {/* Intervals */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading font-black text-base flex-1">Intervals</h2>
          <button
            data-testid="mp-add-interval"
            onClick={addInterval}
            className="btn-ghost h-8 px-2 rounded text-[11px] label-up inline-flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> Add interval
          </button>
        </div>
        {draft.intervals.map((iv, idx) => (
          <IntervalCard
            key={idx}
            interval={iv}
            idx={idx}
            canRemove={draft.intervals.length > 1}
            onUpdate={(patch) => updateInterval(idx, patch)}
            onTri={(key, leg, value) => updateTri(idx, key, leg, value)}
            onRemove={() => removeInterval(idx)}
          />
        ))}
      </section>

      {/* RC-1 Editor Save Consistency — primary Save anchored at the bottom. */}
      <div data-testid="mud-program-editor-bottom-bar"
           className="card-rs p-4 flex flex-wrap items-center justify-end gap-2">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/mud-programs`)}
                data-testid="mud-program-editor-back-bottom"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[var(--rs-text-2)] border border-[var(--rs-border,#1f2630)] hover:bg-[var(--rs-surface-2,#12161d)] order-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button
          data-testid="mud-program-editor-save"
          onClick={onSave}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold order-1"
          style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
        >
          <Save className="w-4 h-4" /> Save Mud Program
        </button>
      </div>

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function IntervalCard({ interval, idx, canRemove, onUpdate, onTri, onRemove }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="card-rs p-3" data-testid={`mp-interval-${idx}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="btn-ghost h-7 w-7 rounded text-[14px]"
          data-testid={`mp-interval-${idx}-toggle`}
        >
          {open ? "▾" : "▸"}
        </button>
        <Field
          label="Label"
          testid={`mp-iv-${idx}-label`}
          value={interval.label}
          onChange={(v) => onUpdate({ label: v })}
          compact
        />
        <Field
          label="Hole Section"
          testid={`mp-iv-${idx}-section`}
          value={interval.holeSection}
          onChange={(v) => onUpdate({ holeSection: v })}
          compact
        />
        {canRemove && (
          <button
            onClick={onRemove}
            data-testid={`mp-iv-${idx}-remove`}
            className="btn-ghost h-8 w-8 rounded inline-flex items-center justify-center"
          >
            <Trash2 className="w-3.5 h-3.5 text-rose-400" />
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Top (ft)"
              testid={`mp-iv-${idx}-top`}
              value={interval.topDepthFt}
              onChange={(v) => onUpdate({ topDepthFt: v })}
              compact
              numeric
            />
            <Field
              label="Bottom (ft)"
              testid={`mp-iv-${idx}-bottom`}
              value={interval.bottomDepthFt}
              onChange={(v) => onUpdate({ bottomDepthFt: v })}
              compact
              numeric
            />
          </div>

          <div className="rounded border overflow-hidden" style={{ borderColor: "var(--rs-border)" }}>
            <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] gap-1 px-2 py-1.5 text-[9.5px] label-up text-[var(--rs-text-3)] border-b" style={{ borderColor: "var(--rs-border)" }}>
              <div>Property</div>
              <div className="text-center">Target</div>
              <div className="text-center">Current</div>
              <div className="text-center">Planned</div>
            </div>
            {MUD_PROPERTY_KEYS.map((k) => {
              const tri = interval.properties?.[k] || { target: "", current: "", planned: "" };
              return (
                <div key={k} className="grid grid-cols-[1.7fr_1fr_1fr_1fr] gap-1 px-2 py-1 text-[11px] items-center border-t" style={{ borderColor: "var(--rs-border)" }}>
                  <div className="text-[var(--rs-text-2)] truncate" title={MUD_PROPERTY_LABELS[k]}>{MUD_PROPERTY_LABELS[k]}</div>
                  <input
                    data-testid={`mp-iv-${idx}-${k}-target`}
                    value={tri.target}
                    onChange={(e) => onTri(k, "target", e.target.value)}
                    className="w-full h-7 px-1.5 rounded border bg-[rgba(10,11,14,0.75)] text-[11px] text-[var(--rs-text-1)] text-center"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                  <input
                    data-testid={`mp-iv-${idx}-${k}-current`}
                    value={tri.current}
                    onChange={(e) => onTri(k, "current", e.target.value)}
                    className="w-full h-7 px-1.5 rounded border bg-[rgba(10,11,14,0.75)] text-[11px] text-[var(--rs-text-1)] text-center"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                  <input
                    data-testid={`mp-iv-${idx}-${k}-planned`}
                    value={tri.planned}
                    onChange={(e) => onTri(k, "planned", e.target.value)}
                    className="w-full h-7 px-1.5 rounded border bg-[rgba(10,11,14,0.75)] text-[11px] text-[var(--rs-text-1)] text-center"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, multiline, compact, numeric }) {
  const Cmp = multiline ? "textarea" : "input";
  return (
    <label className={`block ${compact ? "flex-1 min-w-0" : ""}`}>
      <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-0.5">{label}</div>
      <Cmp
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        inputMode={numeric ? "decimal" : undefined}
        className={`w-full ${multiline ? "min-h-[60px]" : "h-9"} px-2 py-1 rounded border bg-[rgba(10,11,14,0.75)] text-[12px] text-[var(--rs-text-1)]`}
        style={{ borderColor: "var(--rs-border)" }}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-0.5">{label}</div>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2 rounded border bg-[rgba(22,24,29,0.85)] text-[12px] text-[var(--rs-text-1)]"
        style={{ borderColor: "var(--rs-border)" }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
